import type { CollectionDef } from './collection.ts';
import { events } from './modules/calendar.ts';
import { listItems, lists } from './modules/lists.ts';
import { taskCompletions, tasks } from './modules/tasks.ts';
import { mealEntries, recipes } from './modules/meals.ts';
import { albums, photos } from './modules/photos.ts';
import { docFolders, documents } from './modules/vault.ts';
import { petRecords, pets } from './modules/pets.ts';
import { assets, maintenanceItems } from './modules/maintenance.ts';
import { goalEntries, goals } from './modules/goals.ts';
import { emergencyContacts, emergencyInfo } from './modules/emergency.ts';
import { memories } from './modules/memories.ts';
import { comments } from './modules/comments.ts';
import { notices } from './modules/board.ts';

/**
 * Every synchronized collection. Order matters only for readability; parents
 * are listed before their children.
 */
export const ALL_COLLECTIONS = [
  events,
  lists,
  listItems,
  tasks,
  taskCompletions,
  mealEntries,
  recipes,
  albums,
  photos,
  docFolders,
  documents,
  pets,
  petRecords,
  assets,
  maintenanceItems,
  goals,
  goalEntries,
  emergencyContacts,
  emergencyInfo,
  memories,
  notices,
  comments,
] as const satisfies readonly CollectionDef[];

export type CollectionName = (typeof ALL_COLLECTIONS)[number]['name'];

const byName = new Map<string, CollectionDef>(ALL_COLLECTIONS.map((c) => [c.name, c as CollectionDef]));

export function getCollection(name: string): CollectionDef | undefined {
  return byName.get(name);
}

/** Collections whose audience is inherited from `parentName`. */
export function childCollectionsOf(parentName: string): CollectionDef[] {
  return (ALL_COLLECTIONS as readonly CollectionDef[]).filter(
    (c) =>
      c.parent !== undefined &&
      ((c.parent.kind === 'fixed' && c.parent.collection === parentName) || (c.parent.kind === 'dynamic' && c.parent.allowed.includes(parentName))),
  );
}

/** Field holding the file reference(s) for collections that own uploaded files. */
export const FILE_FIELDS: Readonly<Record<string, { field: string; many?: boolean }>> = {
  photos: { field: 'fileId' },
  documents: { field: 'fileId' },
  pets: { field: 'photoFileId' },
  memories: { field: 'fileIds', many: true },
};
